<template>
  <!-- External link -->
  <a
    v-if="href"
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    :class="[
      returnButtonTypeWhenIsMobile,
      icon === 'left' ? 'icon-left' : '',
      icon === 'right' ? 'icon-right' : '',
    ]"
  >
    <span class="icon">
      <slot />
    </span>
    <span class="sg-text">{{ text }}</span>
  </a>

  <!-- Internal link -->
  <router-link
    v-else-if="to"
    :to="to"
    :class="[
      returnButtonTypeWhenIsMobile,
      icon === 'left' ? 'icon-left' : '',
      icon === 'right' ? 'icon-right' : '',
    ]"
  >
    <span class="icon">
      <slot />
    </span>
    <span class="sg-text">{{ text }}</span>
  </router-link>

  <!-- Standard button -->
  <button
    v-else
    :type="type"
    :class="[
      returnButtonTypeWhenIsMobile,
      icon === 'left' ? 'icon-left' : '',
      icon === 'right' ? 'icon-right' : '',
      loading ? 'loading' : '',
    ]"
    :disabled="disabled || loading"
    @click="$emit('btnClick')"
  >
    <EllipsisLoading
      v-if="loading"
      class="ellipsis"
    />
    <span
      v-if="!loading"
      class="icon"
    >
      <slot />
    </span>
    <span
      v-if="!loading"
      class="sg-text"
    >{{ text }}</span>
  </button>
</template>

<script>
import EllipsisLoading from './EllipsisLoading.vue';

export default {
  name: 'ButtonComponent',

  components: {
    EllipsisLoading,
  },

  props: {
    btnStyle: {
      type: String,
      required: false,
      default: 'primary',
    },
    text: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: 'button',
    },
    href: {
      type: String,
      required: false,
      default: '',
    },
    to: {
      type: [String, Object],
      required: false,
      default: '',
    },
  },
  computed: {
    returnButtonTypeWhenIsMobile() {
      let buttonType = this.btnStyle;
      const screenWidth = window.screen.width;

      if (screenWidth <= 575 && this.btnStyle === 'secondary') {
        buttonType = 'tertiary';
      }

      return buttonType;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Common styling */
button,
a {
  border: none;
  border-radius: $sg-btn-radius;
  padding: $sg-btn-padding-height $sg-btn-padding-width;
  height: $sg-btn-height;
  width: fit-content;
  min-width: $sg-btn-min-width;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 0.5rem;
  transition: all ease-in-out 300ms;
  text-decoration: none;

  &:hover:not(.loading, :disabled) {
    cursor: pointer;
  }
}

/* Icon related styling */
.icon {
  display: none;
  align-items: center;

  :first-child {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.icon-left {
  flex-direction: row;

  .icon {
    display: flex;
  }
}

.icon-right {
  flex-direction: row-reverse;

  .icon {
    display: flex;
  }
}

/* Button related styling */
.primary {
  background-color: $primary;

  @include text-btn;

  &:hover:not(.loading, :disabled) {
    background-color: darken($primary, 15%);
    box-shadow: 2px 2px 10px 0 rgb(0 0 0 / 0.1);

    @include text-btn;
  }

  &:disabled:not(.loading) {
    background-color: rgb(0 0 0 / 0.1);

    @include text-btn($color: 2);

    &:hover {
      cursor: not-allowed;
    }
  }

  .ellipsis > :nth-child(n) {
    background: $white;
  }
}

.secondary {
  background-color: $transparent;
  border: 1px solid $primary;

  @include text-btn($color: 3);

  &:hover:not(.loading, :disabled) {
    background-color: $primary-light;

    @include text-btn($color: 3);
  }

  &:disabled:not(.loading) {
    border-color: $btn-disabled-border;

    @include text-btn($color: 2);

    &:hover {
      background-color: $transparent;
      cursor: not-allowed;
    }
  }

  .ellipsis > :nth-child(n) {
    background: $primary;
  }
}

.cancel-primary {
  background-color: $text-btn-negative;

  @include text-btn;

  &:hover:not(.loading) {
    background-color: darken($text-btn-negative, 15%);
    box-shadow: 2px 2px 10px 0 rgb(0 0 0 / 0.1);

    @include text-btn;
  }

  .ellipsis > :nth-child(n) {
    background: $white;
  }
}

.cancel-secondary {
  background-color: $transparent;
  border: 1px solid $text-btn-negative;

  @include text-btn($color: 4);

  &:hover:not(.loading) {
    background-color: #fde9e4;
    box-shadow: 2px 2px 10px 0 rgb(0 0 0 / 0.1);
  }

  .ellipsis > :nth-child(n) {
    background: $text-btn-negative;
  }
}

.tertiary {
  background-color: $transparent;
  padding: 0;
  height: fit-content;
  min-width: 0;

  @include text-btn($color: 3);

  .ellipsis > :nth-child(n) {
    background: $primary;
  }

  &:hover :is(.sg-text) {
    text-decoration: underline;

    @include text-btn($color: 3);
  }
}

.cancel-text {
  background-color: $transparent;
  padding: 0;
  height: fit-content;
  min-width: fit-content;

  @include text-btn($color: 4);

  &:hover :is(.sg-text) {
    text-decoration: underline;

    @include text-btn($color: 4);
  }
}

.cancel-tertiary {
  background-color: $transparent;
  padding: 0;
  height: fit-content;
  min-width: 0;

  @include text-btn($color: 4);

  &:hover:not(:disabled) :is(.sg-text) {
    text-decoration: underline;

    @include text-btn($color: 4);
  }

  &:disabled {
    cursor: not-allowed;

    @include text-btn($color: 2);
  }
}

/* Loading related styling */
.loading:hover {
  cursor: wait;
}
</style>
